import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// 定義介面
interface MediaQueryState {
 isMobile: boolean;
 isWideMobile: boolean;
}

interface MediaMatchers {
 isMobile: MediaQueryList;
 isWideMobile: MediaQueryList;
}

interface MediaQueryProviderProps {
 children: ReactNode;
}

// 創建 context 時提供初始值型別
const MediaQueryContext = createContext<MediaQueryState>({
 isMobile: false,
 isWideMobile: false
});

export const MediaQueryProvider: React.FC<MediaQueryProviderProps> = ({ children }) => {
 const [mediaQueries, setMediaQueries] = useState<MediaQueryState>({
   isMobile: false,    // < 500px
   isWideMobile: false, // 500px ~ 768px

 });

 useEffect(() => {
   // 創建媒體查詢匹配器
   const mediaMatchers: MediaMatchers = {
     isMobile: window.matchMedia('(max-width: 500px)'),
     isWideMobile: window.matchMedia(' (max-width: 768px)'),
   };

   // 更新狀態的處理函數
   const updateMediaQueries = () => {
     setMediaQueries({
       isMobile: mediaMatchers.isMobile.matches,
       isWideMobile: mediaMatchers.isWideMobile.matches,
     });
   };

   // 初始化狀態
   updateMediaQueries();

   // 添加事件監聽器
   Object.values(mediaMatchers).forEach(matcher => {
     // 使用新的 API (現代瀏覽器)
     if (matcher.addEventListener) {
       matcher.addEventListener('change', updateMediaQueries);
     } else {
       // 舊版 API 的 fallback
       matcher.addListener(updateMediaQueries);
     }
   });

   // 清理函數
   return () => {
     Object.values(mediaMatchers).forEach(matcher => {
       if (matcher.removeEventListener) {
         matcher.removeEventListener('change', updateMediaQueries);
       } else {
         matcher.removeListener(updateMediaQueries);
       }
     });
   };
 }, []);

 return (
   <MediaQueryContext.Provider value={mediaQueries}>
     {children}
   </MediaQueryContext.Provider>
 );
};

// 自定義 Hook
export const useMediaQueries = (): MediaQueryState => {
 const context = useContext(MediaQueryContext);
 if (context === undefined) {
   throw new Error('useMediaQueries must be used within a MediaQueryProvider');
 }
 return context;
};

// 創建自定義的媒體查詢 Hook
export const useCustomMediaQuery = (query: string): boolean => {
 const [matches, setMatches] = useState<boolean>(false);

 useEffect(() => {
   const mediaQuery = window.matchMedia(query);
   
   const updateMatch = () => {
     setMatches(mediaQuery.matches);
   };

   // 初始化
   updateMatch();

   // 添加監聽器（使用新的 API）
   if (mediaQuery.addEventListener) {
     mediaQuery.addEventListener('change', updateMatch);
   } else {
     // 舊版 API 的 fallback
     mediaQuery.addListener(updateMatch);
   }

   // 清理
   return () => {
     if (mediaQuery.removeEventListener) {
       mediaQuery.removeEventListener('change', updateMatch);
     } else {
       mediaQuery.removeListener(updateMatch);
     }
   };
 }, [query]);

 return matches;
};